import { createAxiosAgent } from 'src/agent';

const agent = createAxiosAgent();
const CHOICE_KEY = 'tgh_acquisition_analytics_choice_v1';
const STATE_KEY = 'tgh_acquisition_state_v1';
const AUTH_WAIT_MS = 3000;
const EXPIRY_SAFETY_MS = 30_000;
const TGHUNTER_COUNTER_ID = 96484608;

type Choice = 'accepted' | 'rejected' | null;
type CaptureState = {
    capability: string;
    capabilityExpiresAt: number;
    consentEventId: string;
    touchEventId: string;
    observationEventId: string;
    withdrawalEventId: string;
    consentRecorded: boolean;
    touchRecorded: boolean;
    observationRecorded: boolean;
    touch: Record<string, string>;
    handoffToken?: string;
    handoffExpiresAt?: number;
};

let pendingAttribution: Promise<string | null> | null = null;
let consentGeneration = 0;

function uuid() {
    return crypto.randomUUID();
}

function read(storage: Storage, key: string) {
    try {
        return storage.getItem(key) || '';
    } catch {
        return '';
    }
}

function write(storage: Storage, key: string, value: string) {
    try {
        if (value) storage.setItem(key, value);
        else storage.removeItem(key);
    } catch {
        // Analytics storage must never make authentication unavailable.
    }
}

function loadState(): CaptureState | null {
    try {
        const value = JSON.parse(read(sessionStorage, STATE_KEY));
        return value && typeof value.capability === 'string' ? value : null;
    } catch {
        return null;
    }
}

function saveState(state: CaptureState | null) {
    write(sessionStorage, STATE_KEY, state ? JSON.stringify(state) : '');
}

export function analyticsConsentChoice(): Choice {
    const value = read(localStorage, CHOICE_KEY);
    return value === 'accepted' || value === 'rejected' ? value : null;
}

function campaignTouch() {
    const params = new URLSearchParams(window.location.search);
    const allowed = ['yclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
    return Object.fromEntries(allowed.flatMap((key) => {
        const value = params.get(key)?.trim();
        return value ? [[key, value]] : [];
    }));
}

function stillAccepted(generation: number) {
    return generation === consentGeneration && analyticsConsentChoice() === 'accepted';
}

async function freshState(generation: number): Promise<CaptureState | null> {
    const subject = await agent.post('/acquisition/subject/', {});
    if (!stillAccepted(generation)) return null;
    const capability = subject.data?.capability;
    const expiresIn = Number(subject.data?.expires_in || 0);
    if (
        typeof capability !== 'string'
        || capability.length === 0
        || subject.data?.counter_id !== TGHUNTER_COUNTER_ID
        || !Number.isFinite(expiresIn)
        || expiresIn * 1000 <= EXPIRY_SAFETY_MS
    ) return null;
    const state: CaptureState = {
        capability,
        capabilityExpiresAt: Date.now() + expiresIn * 1000,
        consentEventId: uuid(),
        touchEventId: uuid(),
        observationEventId: uuid(),
        withdrawalEventId: uuid(),
        consentRecorded: false,
        touchRecorded: false,
        observationRecorded: false,
        // Campaign fields are stored only after this app's explicit opt-in.
        touch: campaignTouch(),
    };
    saveState(state);
    return state;
}

async function startAcceptedAttribution(generation: number): Promise<string | null> {
    let state = loadState();
    if (!state || state.capabilityExpiresAt <= Date.now() + EXPIRY_SAFETY_MS) {
        state = await freshState(generation);
    }
    if (!state || !stillAccepted(generation)) return null;

    if (!state.consentRecorded) {
        await agent.post('/acquisition/consent/', {
            capability: state.capability,
            event_id: state.consentEventId,
            decision: 'accept',
        });
        if (!stillAccepted(generation)) return null;
        state.consentRecorded = true;
        saveState(state);
    }
    if (!stillAccepted(generation)) return null;

    if (!state.touchRecorded && Object.keys(state.touch).length > 0) {
        await agent.post('/acquisition/touch/', {
            capability: state.capability,
            event_id: state.touchEventId,
            ...state.touch,
        });
        if (!stillAccepted(generation)) return null;
        state.touchRecorded = true;
        saveState(state);
    }
    if (!stillAccepted(generation)) return null;

    if (!state.observationRecorded) {
        await agent.post('/acquisition/observation/', {
            capability: state.capability,
            event_id: state.observationEventId,
            event_type: 'registration_started',
            surface: 'registration',
        });
        if (!stillAccepted(generation)) return null;
        state.observationRecorded = true;
        saveState(state);
    }
    if (!stillAccepted(generation)) return null;

    if (state.handoffToken && (state.handoffExpiresAt || 0) > Date.now() + EXPIRY_SAFETY_MS) {
        return state.handoffToken;
    }
    const handoff = await agent.post('/acquisition/handoff/', { capability: state.capability });
    if (!stillAccepted(generation)) return null;
    const handoffToken = handoff.data?.handoff_token;
    const handoffExpiresIn = Number(handoff.data?.expires_in || 0);
    if (
        typeof handoffToken !== 'string'
        || handoffToken.length === 0
        || !Number.isFinite(handoffExpiresIn)
        || handoffExpiresIn * 1000 <= EXPIRY_SAFETY_MS
    ) return null;
    state.handoffToken = handoffToken;
    state.handoffExpiresAt = Date.now() + handoffExpiresIn * 1000;
    saveState(state);
    return state.handoffToken || null;
}

async function withdrawState(state: CaptureState | null, eventId = state?.withdrawalEventId) {
    saveState(null);
    if (!state?.capability) return;
    try {
        await agent.post('/acquisition/consent/', {
            capability: state.capability,
            event_id: eventId,
            decision: 'withdraw',
        });
    } catch {
        // The local rejection remains authoritative for future browser writes.
    }
}

export async function setAnalyticsConsentChoice(accepted: boolean) {
    const previous = pendingAttribution;
    consentGeneration += 1;
    write(localStorage, CHOICE_KEY, accepted ? 'accepted' : 'rejected');
    pendingAttribution = null;
    if (accepted) {
        await waitForAcquisitionAttribution();
        return;
    }
    const stateAtWithdrawal = loadState();
    await withdrawState(stateAtWithdrawal);
    if (previous) {
        await previous.catch(() => null);
        // This is a later consent decision, not a transport retry of the first
        // withdrawal. A fresh operation id makes it authoritative after a grant
        // whose response arrived late.
        await withdrawState(stateAtWithdrawal, uuid());
    }
}

function withTimeout(value: Promise<string | null>) {
    return Promise.race([
        value,
        new Promise<null>((resolve) => window.setTimeout(() => resolve(null), AUTH_WAIT_MS)),
    ]);
}

export function waitForAcquisitionAttribution(): Promise<string | null> {
    if (analyticsConsentChoice() !== 'accepted') return Promise.resolve(null);
    if (!pendingAttribution) {
        const work = startAcceptedAttribution(consentGeneration).catch(() => null);
        pendingAttribution = work;
        work.finally(() => {
            if (pendingAttribution === work) pendingAttribution = null;
        });
    }
    return withTimeout(pendingAttribution);
}

export async function withAcquisitionHandoff<T extends object>(payload: T): Promise<T & { acquisition_handoff_token?: string }> {
    const token = await waitForAcquisitionAttribution();
    return token ? { ...payload, acquisition_handoff_token: token } : payload;
}
